// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import GuildMembers from '@app/features/member/state/GuildMembers';
import MemberSearch, {type SearchContext, type TransformedMember} from '@app/features/member/state/MemberSearch';
import Navigation from '@app/features/navigation/state/Navigation';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import {parseChannelUrl} from '@app/features/navigation/utils/DeepLinkUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {QuickSwitcherModal} from '@app/features/search/components/quick_switcher/QuickSwitcherModal';
import {buildCandidateSets} from '@app/features/search/state/QuickSwitcherCandidateBuilder';
import {getFirstSelectableIndex} from '@app/features/search/state/QuickSwitcherResultConverters';
import {
	generateDefaultResults,
	generateGeneralResults,
	generateQueryModeResults,
	resolveTransformedMember,
} from '@app/features/search/state/QuickSwitcherResultGenerators';
import type {
	ComputeResultsForQueryResult,
	LinkResult,
	QuickSwitcherExecutableResult,
	QuickSwitcherQueryMode,
	QuickSwitcherResult,
} from '@app/features/search/state/QuickSwitcherTypes';
import {MEMBER_SEARCH_LIMIT, QUICK_SWITCHER_MODAL_KEY} from '@app/features/search/state/QuickSwitcherTypes';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {QuickSwitcherResultTypes} from '@app/vendor/constants/QuickSwitcherConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {action, makeAutoObservable, reaction, runInAction} from 'mobx';

const QUICK_SWITCHER_I18N_MISSING_ERROR = 'QuickSwitcher i18n has not been set';
class QuickSwitcher {
	private logger = new Logger('QuickSwitcher');
	isOpen = false;
	query = '';
	queryMode: QuickSwitcherQueryMode | null = null;
	results: Array<QuickSwitcherResult> = [];
	selectedIndex = -1;
	private memberSearchContext: SearchContext | null = null;
	private memberFetchDebounceTimer: NodeJS.Timeout | null = null;
	private isFetchingMembersInBackground = false;
	private memberSearchResults: Array<GuildMember> = [];
	private i18n: I18n | null = null;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		reaction(
			() => SelectedChannel.recentChannelVisits,
			() => {
				if (this.isOpen) {
					this.recomputeIfOpen();
				}
			},
		);
		reaction(
			() => [Navigation.guildId, Navigation.channelId],
			() => {
				if (this.isOpen) {
					this.recomputeIfOpen();
				}
			},
		);
	}
	setI18n(i18n: I18n): void {
		this.i18n = i18n;
	}
	private getI18n(): I18n {
		if (!this.i18n) {
			throw new Error(QUICK_SWITCHER_I18N_MISSING_ERROR);
		}
		return this.i18n;
	}
	getIsOpen(): boolean {
		return this.isOpen;
	}
	getResults(): ReadonlyArray<QuickSwitcherResult> {
		return this.results;
	}
	getSelectedResult(): QuickSwitcherExecutableResult | null {
		if (this.selectedIndex < 0 || this.selectedIndex >= this.results.length) {
			return null;
		}
		const result = this.results[this.selectedIndex];
		if (result.type === QuickSwitcherResultTypes.HEADER) {
			return null;
		}
		return result;
	}
	get isLoadingMemberResults(): boolean {
		return (
			this.queryMode === QuickSwitcherResultTypes.USER &&
			this.query['slice'](1).trim().length > 0 &&
			(this.memberFetchDebounceTimer !== null || this.isFetchingMembersInBackground)
		);
	}
	findNextSelectableIndex(direction: 'up' | 'down', startIndex?: number): number {
		if (this.results.length === 0) return -1;
		let index = startIndex ?? this.selectedIndex;
		const step = direction === 'down' ? 1 : -1;
		for (let i = 0; i < this.results.length; i += 1) {
			index += step;
			if (index < 0) index = this.results.length - 1;
			if (index >= this.results.length) index = 0;
			if (this.results[index].type !== QuickSwitcherResultTypes.HEADER) {
				return index;
			}
		}
		return this.selectedIndex;
	}
	@action
	show(): void {
		if (this.isOpen) return;
		this.isOpen = true;
		this.query = '';
		this.queryMode = null;
		try {
			const {results, selectedIndex} = this.computeResultsForQuery('');
			this.results = results;
			this.selectedIndex = selectedIndex;
		} catch (error) {
			if (error instanceof Error && error.message === QUICK_SWITCHER_I18N_MISSING_ERROR) {
				throw error;
			}
			this.logger.error('Quick switcher failed to precompute results', error);
			this.results = [];
			this.selectedIndex = -1;
		}
		if (!MobileLayout.isMobileLayout()) {
			void this.pushModal();
		}
	}
	private pushModal(): void {
		ModalCommands.pushWithKey(
			modal(() => <QuickSwitcherModal />),
			QUICK_SWITCHER_MODAL_KEY,
		);
	}
	@action
	hide(): void {
		if (!this.isOpen) {
			return;
		}
		this.isOpen = false;
		this.query = '';
		this.queryMode = null;
		this.results = [];
		this.selectedIndex = -1;
		if (this.memberSearchContext) {
			this.memberSearchContext.destroy();
			this.memberSearchContext = null;
		}
		if (this.memberFetchDebounceTimer) {
			clearTimeout(this.memberFetchDebounceTimer);
			this.memberFetchDebounceTimer = null;
		}
		this.isFetchingMembersInBackground = false;
		this.memberSearchResults = [];
		if (!MobileLayout.isMobileLayout()) {
			ModalCommands.popWithKey(QUICK_SWITCHER_MODAL_KEY);
		}
	}
	@action
	search(query: string): void {
		if (!this.isOpen && query.length === 0) {
			return;
		}
		const {queryMode, results, selectedIndex} = this.computeResultsForQuery(query);
		this.query = query;
		this.queryMode = queryMode;
		this.results = results;
		this.selectedIndex = selectedIndex;
		this.triggerMemberSearchIfNeeded(query, queryMode);
	}
	private triggerMemberSearchIfNeeded(query: string, queryMode: QuickSwitcherQueryMode | null): void {
		if (queryMode !== QuickSwitcherResultTypes.USER) {
			if (this.memberSearchContext) {
				this.memberSearchContext.destroy();
				this.memberSearchContext = null;
			}
			if (this.memberFetchDebounceTimer) {
				clearTimeout(this.memberFetchDebounceTimer);
				this.memberFetchDebounceTimer = null;
			}
			this.isFetchingMembersInBackground = false;
			this.memberSearchResults = [];
			return;
		}
		const rawSearch = query['slice'](1).trim();
		if (rawSearch.length === 0) {
			if (this.memberSearchContext) {
				this.memberSearchContext.clearQuery();
			}
			if (this.memberFetchDebounceTimer) {
				clearTimeout(this.memberFetchDebounceTimer);
				this.memberFetchDebounceTimer = null;
			}
			this.isFetchingMembersInBackground = false;
			this.memberSearchResults = [];
			return;
		}
		if (!this.memberSearchContext) {
			this.memberSearchContext = MemberSearch.getSearchContext((results) => {
				const guildMemberRecords: Array<GuildMember> = results
					.map((transformed: TransformedMember) => resolveTransformedMember(transformed))
					.filter((member): member is GuildMember => member !== null);
				runInAction(() => {
					this.memberSearchResults = guildMemberRecords;
					if (this.isOpen && this.queryMode === QuickSwitcherResultTypes.USER) {
						this.recomputeIfOpen();
					}
				});
			}, MEMBER_SEARCH_LIMIT);
		}
		this.memberSearchContext.setQuery(rawSearch);
		if (this.memberFetchDebounceTimer) {
			clearTimeout(this.memberFetchDebounceTimer);
		}
		const currentChannelId = SelectedChannel.currentChannelId;
		const currentChannel = currentChannelId ? Channels.getChannel(currentChannelId) : null;
		const guildId = currentChannel?.guildId ?? null;
		const allGuilds = Guilds.getGuilds();
		const guildsToFetch = allGuilds
			.filter((guild) => !GuildMembers.isGuildFullyLoaded(guild.id))
			.map((guild) => guild.id);
		if (guildsToFetch.length === 0) {
			this.memberFetchDebounceTimer = null;
			this.isFetchingMembersInBackground = false;
			return;
		}
		this.memberFetchDebounceTimer = setTimeout(() => {
			this.isFetchingMembersInBackground = true;
			void MemberSearch.fetchMembersInBackground(rawSearch, guildsToFetch, guildId ?? undefined).finally(() => {
				runInAction(() => {
					this.isFetchingMembersInBackground = false;
				});
			});
			this.memberFetchDebounceTimer = null;
		}, 300);
	}
	@action
	select(selectedIndex: number): void {
		if (!this.isOpen) {
			return;
		}
		if (selectedIndex < 0) {
			this.selectedIndex = -1;
			return;
		}
		if (selectedIndex >= this.results.length) {
			return;
		}
		const result = this.results[selectedIndex];
		if (result.type === QuickSwitcherResultTypes.HEADER) {
			this.selectedIndex = -1;
			return;
		}
		this.selectedIndex = selectedIndex;
	}
	recomputeIfOpen(): void {
		if (!this.isOpen) {
			return;
		}
		const {queryMode, results, selectedIndex} = this.computeResultsForQuery(this.query);
		this.queryMode = queryMode;
		this.results = results;
		this.selectedIndex = selectedIndex;
	}
	private computeResultsForQuery(query: string): ComputeResultsForQueryResult {
		const i18n = this.getI18n();
		const sets = buildCandidateSets(i18n);
		const channelPath = parseChannelUrl(query);
		if (channelPath) {
			const linkResult: LinkResult = {
				type: QuickSwitcherResultTypes.LINK,
				id: 'link-jump',
				title: i18n._(msg`Go to message`),
				subtitle: query,
				path: channelPath,
			};
			return {
				queryMode: null,
				results: [linkResult],
				selectedIndex: 0,
			};
		}
		if (query['trim']().length === 0) {
			const results = generateDefaultResults(sets, i18n);
			return {
				queryMode: null,
				results,
				selectedIndex: getFirstSelectableIndex(results),
			};
		}
		const queryMode = this.getQueryMode(query);
		const rawSearch = queryMode ? query['slice'](1) : query;
		const trimmedSearch = rawSearch.trim();
		let results: Array<QuickSwitcherResult>;
		if (queryMode) {
			results = generateQueryModeResults(queryMode, trimmedSearch, sets, i18n, this.memberSearchResults);
		} else if (trimmedSearch.length === 0) {
			results = generateDefaultResults(sets, i18n);
		} else {
			results = generateGeneralResults(trimmedSearch, sets, i18n);
		}
		return {
			queryMode,
			results,
			selectedIndex: getFirstSelectableIndex(results),
		};
	}
	private getQueryMode(query: string): QuickSwitcherQueryMode | null {
		switch (query.charAt(0)) {
			case '@':
				return QuickSwitcherResultTypes.USER;
			case '#':
				return QuickSwitcherResultTypes.TEXT_CHANNEL;
			case '!':
				return QuickSwitcherResultTypes.VOICE_CHANNEL;
			case '*':
				return QuickSwitcherResultTypes.GUILD;
			case '>':
				return QuickSwitcherResultTypes.QUICK_ACTION;
			default:
				return null;
		}
	}
}

export default new QuickSwitcher();
